import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import {
  CalendarClock,
  Bell,
  ExternalLink,
  ArrowRight,
  AlertTriangle,
} from "lucide-react";
import { Link } from "wouter";
import { usePermissions } from "@/hooks/usePermissions";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

interface EnrollmentDeadline {
  employeeId: number;
  employeeName: string;
  clientId: number;
  clientName: string;
  planId: number;
  planName: string;
  enrollmentDueDate: string;
  daysUntilDue: number;
  effectiveDate: string;
}

interface EnrollmentDeadlinesResponse {
  employees: EnrollmentDeadline[];
  totalCount: number;
}

function getUrgencyBadge(daysUntilDue: number) {
  if (daysUntilDue <= 1) {
    return (
      <Badge variant="destructive" className="text-xs">
        Due {daysUntilDue === 0 ? "Today" : "Tomorrow"}
      </Badge>
    );
  }
  if (daysUntilDue <= 3) {
    return (
      <Badge variant="destructive" className="bg-orange-100 text-orange-800 hover:bg-orange-100 text-xs">
        {daysUntilDue} days left
      </Badge>
    );
  }
  if (daysUntilDue <= 7) {
    return (
      <Badge variant="secondary" className="bg-yellow-100 text-yellow-800 hover:bg-yellow-100 text-xs">
        {daysUntilDue} days left
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className="text-xs">
      {daysUntilDue} days left
    </Badge>
  );
}

function formatDate(dateStr: string): string {
  // Parse as local date to avoid timezone issues with date-only strings
  // "2026-02-04" should display as Feb 4, not Feb 3 in US timezones
  const [year, month, day] = dateStr.split("-").map(Number);
  const date = new Date(year, month - 1, day);
  return date.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });
}

export function UpcomingEnrollmentDeadlines() {
  const { can, isLoading: permissionsLoading } = usePermissions();
  const hasPermission = can("clients:view");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<EnrollmentDeadlinesResponse>({
    queryKey: ["/api/agency/enrollment-deadlines", { limit: 5, days: 21 }],
    queryFn: async () => {
      const response = await fetch("/api/agency/enrollment-deadlines?limit=5&days=21");
      if (!response.ok) throw new Error("Failed to fetch");
      return response.json();
    },
    enabled: hasPermission,
  });

  const sendReminderMutation = useMutation({
    mutationFn: async ({ employeeId, planId }: { employeeId: number; planId: number }) => {
      const res = await apiRequest("POST", "/api/reminders/send", { employeeId, planId });
      return res.json();
    },
    onSuccess: (data) => {
      toast({
        title: "Reminder Sent",
        description: data.message || "Enrollment reminder sent successfully.",
      });
    },
    onError: (error: any) => {
      toast({
        title: "Failed to Send Reminder",
        description: error.message || "Could not send the reminder.",
        variant: "destructive",
      });
    },
  });

  // Don't render if user doesn't have permission
  if (!permissionsLoading && !hasPermission) {
    return null;
  }

  if (isLoading) {
    return (
      <Card>
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="text-sm font-medium">
            Upcoming Enrollment Deadlines
          </CardTitle>
          <CalendarClock className="h-4 w-4 text-muted-foreground" />
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {[...Array(3)].map((_, i) => (
              <div key={i} className="flex items-start gap-3">
                <Skeleton className="h-8 w-8 rounded-full" />
                <div className="flex-1 space-y-2">
                  <Skeleton className="h-4 w-3/4" />
                  <Skeleton className="h-3 w-1/2" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  const deadlines = data?.employees || [];

  // Check if there are urgent deadlines (due in 3 days or less)
  const urgentCount = deadlines.filter((d) => d.daysUntilDue <= 3).length;

  return (
    <Card className={urgentCount > 0 ? "border-orange-200" : ""}>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <div className="flex items-center gap-2">
          <CardTitle className="text-sm font-medium">
            Upcoming Enrollment Deadlines
          </CardTitle>
          {urgentCount > 0 && (
            <Badge variant="destructive" className="text-xs">
              {urgentCount} urgent
            </Badge>
          )}
        </div>
        <CalendarClock className="h-4 w-4 text-muted-foreground" />
      </CardHeader>
      <CardContent>
        {deadlines.length === 0 ? (
          <div className="text-center py-6">
            <CalendarClock className="h-8 w-8 text-gray-400 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No upcoming deadlines</p>
            <p className="text-xs text-gray-400 mt-1">
              All employees are enrolled or past due date
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {deadlines.map((deadline) => (
              <div
                key={`${deadline.employeeId}-${deadline.planId}`}
                className={`flex items-start gap-3 p-2 rounded-lg transition-colors ${
                  deadline.daysUntilDue <= 3
                    ? "bg-orange-50 dark:bg-orange-950/20"
                    : "hover:bg-muted/50"
                }`}
              >
                <div className="mt-0.5">
                  {deadline.daysUntilDue <= 3 ? (
                    <AlertTriangle className="h-4 w-4 text-orange-500" />
                  ) : (
                    <CalendarClock className="h-4 w-4 text-muted-foreground" />
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Link
                      href={`/clients/${deadline.clientId}/employees/${deadline.employeeId}`}
                      className="font-medium text-sm hover:text-primary hover:underline truncate"
                    >
                      {deadline.employeeName}
                    </Link>
                    {getUrgencyBadge(deadline.daysUntilDue)}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">
                    {deadline.planName}
                  </p>
                  <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                    <span className="truncate">{deadline.clientName}</span>
                    <span>•</span>
                    <span>Due {formatDate(deadline.enrollmentDueDate)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-7 w-7 p-0"
                    title="Send reminder"
                    disabled={sendReminderMutation.isPending}
                    onClick={() => sendReminderMutation.mutate({
                      employeeId: deadline.employeeId,
                      planId: deadline.planId,
                    })}
                  >
                    <Bell className="h-3.5 w-3.5" />
                  </Button>
                  <Link
                    href={`/clients/${deadline.clientId}/employees/${deadline.employeeId}`}
                  >
                    <Button
                      variant="ghost"
                      size="sm"
                      className="h-7 w-7 p-0"
                      title="View details"
                    >
                      <ExternalLink className="h-3.5 w-3.5" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}

        {deadlines.length > 0 && (
          <div className="mt-4 pt-3 border-t">
            <Link href="/enrollment-deadlines">
              <Button variant="ghost" size="sm" className="w-full">
                View All Deadlines ({data?.totalCount || 0})
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </Link>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
